import { Suspense, lazy, type ReactNode } from 'react'
import { Navigate, createBrowserRouter } from 'react-router-dom'
import { LoadingBlock } from '@/components/ui/Surface'

/**
 * Routing.
 *
 * THE IMPORTANT STRUCTURAL DECISION: the authenticated half of the product is
 * behind a lazy boundary (`PrivateLayout` / `PublicAuthLayout`), and nothing in
 * this module statically imports the Firebase Auth or Firestore SDKs.
 *
 * A diner opening `/book/:slug` therefore downloads the app shell, the
 * callable client and App Check — not the ~467 kB of Auth and Firestore they
 * will never use. Splitting those into separate build chunks was not enough on
 * its own: a chunk the entry module statically imports is still fetched and
 * executed before first paint.
 *
 * Route guards live inside those lazy layouts, and are a convenience only.
 * Firestore Security Rules and the authorisation checks in every Cloud
 * Function are the actual boundary.
 */

// --- public: no Firebase Auth, no Firestore ---------------------------------
const BookPage = lazy(() => import('@/pages/BookPage'))
const ManageBookingPage = lazy(() => import('@/pages/ManageBookingPage'))
const OfflinePage = lazy(() => import('@/pages/OfflinePage'))
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'))

// --- lazy boundaries --------------------------------------------------------
const PublicAuthLayout = lazy(() => import('@/app/PublicAuthLayout'))
const PrivateLayout = lazy(() => import('@/app/PrivateLayout'))
const RestaurantLayout = lazy(() => import('@/app/RestaurantLayout'))
const AdminLayout = lazy(() => import('@/app/AdminLayout'))

// --- authenticated pages ----------------------------------------------------
const LoginPage = lazy(() => import('@/pages/LoginPage'))
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage'))
const SignupPage = lazy(() => import('@/pages/SignupPage'))
const AppIndexPage = lazy(() => import('@/pages/AppIndexPage'))
const DashboardPage = lazy(() => import('@/pages/DashboardPage'))
const BookingsPage = lazy(() => import('@/pages/BookingsPage'))
const BookingDetailPage = lazy(() => import('@/pages/BookingDetailPage'))
const NewBookingPage = lazy(() => import('@/pages/NewBookingPage'))
const GuestsPage = lazy(() => import('@/pages/GuestsPage'))
const CampaignsPage = lazy(() => import('@/pages/CampaignsPage'))
const CampaignFormPage = lazy(() => import('@/pages/CampaignFormPage'))
const CampaignDetailPage = lazy(() => import('@/pages/CampaignDetailPage'))
const ReportsPage = lazy(() => import('@/pages/ReportsPage'))
const SettingsPage = lazy(() => import('@/pages/SettingsPage'))
const TeamPage = lazy(() => import('@/pages/TeamPage'))
const BillingPage = lazy(() => import('@/pages/BillingPage'))

// --- platform console -------------------------------------------------------
// Its own chunk: only platform admins ever load it, and it is the one part of
// the app that reads across every tenant.
const AdminOverviewPage = lazy(() => import('@/pages/admin/AdminOverviewPage'))
const AdminRestaurantsPage = lazy(() => import('@/pages/admin/AdminRestaurantsPage'))
const AdminUsersPage = lazy(() => import('@/pages/admin/AdminUsersPage'))

function PageFallback() {
  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-8">
      <LoadingBlock label="Loading page" />
    </div>
  )
}

function B({ children }: { children: ReactNode }) {
  return <Suspense fallback={<PageFallback />}>{children}</Suspense>
}

export const router = createBrowserRouter([
  // ---- public booking surfaces ------------------------------------------
  { path: '/book/:restaurantSlug', element: <B><BookPage /></B> },
  { path: '/book/:restaurantSlug/c/:campaignPublicId', element: <B><BookPage /></B> },
  { path: '/manage-booking/:token', element: <B><ManageBookingPage /></B> },

  // ---- auth --------------------------------------------------------------
  {
    element: <B><PublicAuthLayout /></B>,
    children: [
      { path: '/login', element: <B><LoginPage /></B> },
      { path: '/reset-password', element: <B><ResetPasswordPage /></B> },
      { path: '/signup', element: <B><SignupPage /></B> },
    ],
  },

  // ---- dashboard ---------------------------------------------------------
  {
    element: <B><PrivateLayout /></B>,
    children: [
      { path: '/app', element: <B><AppIndexPage /></B> },
      { path: '/app/billing', element: <B><BillingPage /></B> },
      // Declared before `/app/:restaurantId` for readability. React Router
      // ranks a static segment above a dynamic one regardless of order, so
      // `admin` is never mistaken for a restaurant id — but relying on that
      // silently would be a nasty thing to debug if it ever changed.
      {
        path: '/app/admin',
        element: <B><AdminLayout /></B>,
        children: [
          { index: true, element: <B><AdminOverviewPage /></B> },
          { path: 'restaurants', element: <B><AdminRestaurantsPage /></B> },
          { path: 'users', element: <B><AdminUsersPage /></B> },
        ],
      },
      {
        path: '/app/:restaurantId',
        element: <B><RestaurantLayout /></B>,
        children: [
          { index: true, element: <B><DashboardPage /></B> },
          { path: 'bookings', element: <B><BookingsPage /></B> },
          { path: 'bookings/new', element: <B><NewBookingPage /></B> },
          { path: 'bookings/:bookingId', element: <B><BookingDetailPage /></B> },
          { path: 'guests', element: <B><GuestsPage /></B> },
          { path: 'campaigns', element: <B><CampaignsPage /></B> },
          { path: 'campaigns/new', element: <B><CampaignFormPage /></B> },
          { path: 'campaigns/:campaignId', element: <B><CampaignDetailPage /></B> },
          { path: 'campaigns/:campaignId/edit', element: <B><CampaignFormPage /></B> },
          { path: 'reports', element: <B><ReportsPage /></B> },
          { path: 'settings', element: <B><SettingsPage /></B> },
          { path: 'settings/:tab', element: <B><SettingsPage /></B> },
          { path: 'team', element: <B><TeamPage /></B> },
        ],
      },
    ],
  },

  // ---- misc --------------------------------------------------------------
  { path: '/offline', element: <B><OfflinePage /></B> },
  { path: '/', element: <Navigate to="/app" replace /> },
  { path: '*', element: <B><NotFoundPage /></B> },
])
